import React, { useEffect, useMemo, useState } from 'react';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { contentApiService } from '../../api/contentApi';
import { API_BASE_URL, usersAPI } from '../../api/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

const CONTENT_TYPE_LABELS = {
  story: 'Hikaye',
  post: 'Post',
  reels: 'Reels',
  shorts: 'Shorts',
  video: 'Video',
  carousel: 'Karosel',
};

const backendBaseUrl = API_BASE_URL.replace(/\/api$/, '');

const resolvePhotoUrl = (photo) => {
  if (!photo?.file_url) return '';
  if (photo.file_url.startsWith('http://') || photo.file_url.startsWith('https://')) return photo.file_url;
  return `${backendBaseUrl}${photo.file_url}`;
};

const fetchImageAsBase64 = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Image fetch failed');
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getPostPhotos = (row) => {
  const rawPhotos = Array.isArray(row?.photos) ? row.photos : [];
  if (rawPhotos.length > 0) {
    return rawPhotos.map((photo) => resolvePhotoUrl(photo)).filter(Boolean);
  }

  const grouped = row?.grouped_photos || {};
  const merged = [
    ...(grouped.post_grid || []),
    ...(grouped.original || []),
    ...(grouped.approved || []),
  ];
  return merged.map((photo) => resolvePhotoUrl(photo)).filter(Boolean);
};

const InstagramOrderPreview = ({ previewItems = [] }) => {
  if (!previewItems.length) {
    return <div className="text-xs text-gray-400 text-center">Onizleme yok</div>;
  }

  return (
    <div className="flex items-center justify-center gap-2">
      {previewItems.map((item, idx) => (
        <div key={`${item.id}-${idx}`} className="flex flex-col items-center gap-1">
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.content_name || 'post'}
              className={`h-60 w-40 object-cover rounded border ${idx === 0 ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200 dark:border-gray-700'}`}
            />
          ) : (
            <div className={`h-60 w-40 rounded border flex items-center justify-center text-[10px] text-gray-400 ${idx === 0 ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200 dark:border-gray-700'}`}>
              Gorsel yok
            </div>
          )}
          <span className="text-[10px] text-gray-500">{idx + 1}</span>
        </div>
      ))}
    </div>
  );
};

  const ContentPresentationTable = () => {
  const [plans, setPlans] = useState([]);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [filters, setFilters] = useState({
    account_name: '',
    content_type: '',
    responsible_person: '',
    q: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansResponse, channelsResponse, usersResponse] = await Promise.all([
        contentApiService.getPlans(),
        contentApiService.getChannels(),
        usersAPI.getUsers(),
      ]);
      setPlans(plansResponse?.success ? plansResponse.data || [] : []);
      setChannels(channelsResponse?.success ? channelsResponse.data || [] : []);
      setUsers(usersResponse?.success ? usersResponse.data || [] : []);
    } finally {
      setLoading(false);
    }
  };

  const resolveChannelName = (row) => {
    if (typeof row.channel === 'object' && row.channel?.channel_name) return row.channel.channel_name;
    const match = channels.find((c) => c.id === row.channel);
    return match?.channel_name || '-';
  };

  const filteredData = useMemo(() => {
    return plans.filter((row) => {
      if (filters.account_name && row.account_name !== filters.account_name) return false;
      if (filters.content_type && row.content_type !== filters.content_type) return false;
      if (filters.responsible_person && String(row.responsible_person || '') !== String(filters.responsible_person)) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const text = `${row.description || ''} ${row.post_theme || ''} ${row.hashtag || ''} ${row.responsible_person_name || ''}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [plans, filters]);

  const instagramPreviewMap = useMemo(() => {
    const map = {};
    const groupedByAccount = {};

    plans.forEach((row) => {
      const accountKey = row.account_name || 'no-account';
      if (!groupedByAccount[accountKey]) groupedByAccount[accountKey] = [];
      groupedByAccount[accountKey].push(row);
    });

    Object.values(groupedByAccount).forEach((rows) => {
      const sortedPlans = [...rows].sort((a, b) => {
        const aDate = a.share_date || '';
        const bDate = b.share_date || '';
        if (aDate !== bDate) return bDate.localeCompare(aDate);
        return (b.id || 0) - (a.id || 0);
      });

      // Hesap bazında IG karo akışını düzleştir:
      // Bir kayıt 2-3 foto planladıysa akışta 2-3 slot kaplar.
      const flatTiles = [];
      const firstTileIndexByPlan = {};

      sortedPlans.forEach((plan) => {
        const photos = getPostPhotos(plan);
        const tilesForPlan = photos.length > 0 ? photos : [null];
        firstTileIndexByPlan[plan.id] = flatTiles.length;
        tilesForPlan.forEach((imageUrl) => {
          flatTiles.push({
            id: plan.id,
            content_name: plan.content_name,
            imageUrl,
          });
        });
      });

      sortedPlans.forEach((plan) => {
        const startIndex = firstTileIndexByPlan[plan.id];
        const previewItems = flatTiles.slice(startIndex, startIndex + 3);
        map[plan.id] = {
          order: startIndex + 1,
          previewItems,
        };
      });
    });

    return map;
  }, [plans]);

  const columns = useMemo(
    () => [
      { accessorKey: 'content_type', header: 'Post Turu', cell: ({ row }) => CONTENT_TYPE_LABELS[row.original.content_type] || row.original.content_type || '-' },
      { accessorKey: 'responsible_person_name', header: 'Sorumlu Kisi', cell: ({ row }) => row.original.responsible_person_name || '-' },
      { accessorKey: 'description', header: 'Post Metini', cell: ({ row }) => <div className="max-w-[340px] whitespace-normal text-xs text-center mx-auto">{row.original.description || '-'}</div> },
      { accessorKey: 'post_theme', header: 'Post Temasi', cell: ({ row }) => row.original.post_theme || '-' },
      { accessorKey: 'hashtag', header: 'Hashtag', cell: ({ row }) => <div className="max-w-[220px] whitespace-normal text-xs text-center mx-auto">{row.original.hashtag || '-'}</div> },
      {
        id: 'grid_order',
        header: 'Izgara Sirasi',
        cell: ({ row }) => {
          const preview = instagramPreviewMap[row.original.id];
          return preview ? `#${preview.order}` : '-';
        },
      },
      {
        id: 'images',
        header: 'IG Izgara Onizleme',
        cell: ({ row }) => {
          const preview = instagramPreviewMap[row.original.id];
          return <InstagramOrderPreview previewItems={preview?.previewItems || []} />;
        },
      },
    ],
    [instagramPreviewMap]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('ContentPresentation');

    worksheet.columns = [
      { header: 'Account', key: 'account', width: 16 },
      { header: 'Post Turu', key: 'type', width: 14 },
      { header: 'Sorumlu Kisi', key: 'responsible', width: 22 },
      { header: 'Post Metini', key: 'text', width: 36 },
      { header: 'Post Temasi', key: 'theme', width: 18 },
      { header: 'Hashtag', key: 'hashtag', width: 28 },
      { header: 'Izgara Sirasi', key: 'order', width: 12 },
      { header: 'Kanal', key: 'channel', width: 18 },
      { header: 'Gorsel 1', key: 'img1', width: 18 },
      { header: 'Gorsel 2', key: 'img2', width: 18 },
      { header: 'Gorsel 3', key: 'img3', width: 18 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.height = 22;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    const imageTasks = [];
    filteredData.forEach((row) => {
      const preview = instagramPreviewMap[row.id];
      const previewImages = (preview?.previewItems || []).map((item) => item.imageUrl).filter(Boolean).slice(0, 3);

      const excelRow = worksheet.addRow({
        account: row.account_name || '',
        type: CONTENT_TYPE_LABELS[row.content_type] || row.content_type || '',
        responsible: row.responsible_person_name || '',
        text: row.description || '',
        theme: row.post_theme || '',
        hashtag: row.hashtag || '',
        order: preview?.order ? `#${preview.order}` : '',
        channel: resolveChannelName(row),
      });

      // Programdaki onizlemeye daha yakin gorunsun diye satiri buyut
      excelRow.height = 150;
      excelRow.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });

      previewImages.forEach((url, idx) => {
        imageTasks.push({
          rowNumber: excelRow.number,
          colNumber: 9 + idx, // I, J, K
          url,
        });
      });
    });

    for (const task of imageTasks) {
      try {
        const base64 = await fetchImageAsBase64(task.url);
        const extension = base64.includes('image/png') ? 'png' : 'jpeg';
        const imageId = workbook.addImage({
          base64,
          extension,
        });
        worksheet.addImage(imageId, {
          tl: { col: task.colNumber - 1 + 0.1, row: task.rowNumber - 1 + 0.1 },
          // Programdaki dikey kart oranina yakin (w-40 / h-60 benzeri)
          ext: { width: 120, height: 180 },
        });
      } catch (error) {
        // Skip image silently if remote fetch fails
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `content-presentation-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPdf = async () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 24;
    const topHeaderHeight = 58;
    const cardGap = 12;
    const cardHeight = 148;
    const cardWidth = pageWidth - marginX * 2;
    const imageWidth = 76;
    const imageHeight = 116;
    const imageGap = 8;
    const imageBlockWidth = imageWidth * 3 + imageGap * 2;
    const imageStartX = marginX + cardWidth - imageBlockWidth - 14;
    const textStartX = marginX + 14;
    const textWidth = imageStartX - textStartX - 20;
    const imageCache = {};

    const drawPageHeader = (isFirstPage = false) => {
      if (isFirstPage) {
        doc.setFillColor(15, 23, 42);
        doc.rect(0, 0, pageWidth, topHeaderHeight, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('Content Presentation Report', marginX, 26);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(`Olusturma Tarihi: ${new Date().toLocaleString('tr-TR')}`, marginX, 44);
        doc.text(`Kayit Sayisi: ${filteredData.length}`, pageWidth - marginX - 130, 44);
      } else {
        doc.setFillColor(241, 245, 249);
        doc.rect(0, 0, pageWidth, 30, 'F');
        doc.setTextColor(51, 65, 85);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Content Presentation Report', marginX, 19);
      }
    };

    drawPageHeader(true);
    let y = topHeaderHeight + 14;

    for (const row of filteredData) {
      if (y + cardHeight > pageHeight - 24) {
        doc.addPage();
        drawPageHeader(false);
        y = 40;
      }

      const preview = instagramPreviewMap[row.id];
      const previewItems = preview?.previewItems || [];
      const gridOrderText = preview?.order ? `#${preview.order}` : '-';

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(marginX, y, cardWidth, cardHeight, 8, 8, 'FD');

      // Metinler tek satırda, yönetim panelindeki kolon sırası gibi
      doc.setTextColor(30, 41, 59);
      const inlineFields = [
        { label: 'Post Turu', value: CONTENT_TYPE_LABELS[row.content_type] || row.content_type || '-' },
        { label: 'Sorumlu Kisi', value: row.responsible_person_name || '-' },
        { label: 'Post Metini', value: row.description || '-' },
        { label: 'Post Temasi', value: row.post_theme || '-' },
        { label: 'Hashtag', value: row.hashtag || '-' },
        { label: 'Izgara Sirasi', value: gridOrderText },
        { label: 'Kanal', value: resolveChannelName(row) },
      ];
      const fieldGap = 6;
      const fieldWidth = (textWidth - fieldGap * (inlineFields.length - 1)) / inlineFields.length;
      // Metin bloğunu görsel alanın dikey merkezine hizala
      const imageCenterY = y + 16 + (imageHeight / 2);
      const inlineLabelY = imageCenterY - 6;
      const inlineValueY = imageCenterY + 10;

      inlineFields.forEach((field, idx) => {
        const fieldX = textStartX + idx * (fieldWidth + fieldGap);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.8);
        doc.text(field.label, fieldX + fieldWidth / 2, inlineLabelY, { align: 'center' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.2);
        const valueLine = doc.splitTextToSize(String(field.value || '-'), fieldWidth - 4).slice(0, 1);
        doc.text(valueLine, fieldX + fieldWidth / 2, inlineValueY, { align: 'center' });
      });

      for (let i = 0; i < 3; i += 1) {
        const item = previewItems[i];
        const imgX = imageStartX + i * (imageWidth + imageGap);
        const imgY = y + 16;

        doc.setDrawColor(i === 0 ? 59 : 203, i === 0 ? 130 : 213, i === 0 ? 246 : 225);
        doc.setLineWidth(i === 0 ? 1.4 : 0.8);
        doc.roundedRect(imgX, imgY, imageWidth, imageHeight, 4, 4, 'S');

        if (item?.imageUrl) {
          try {
            if (!imageCache[item.imageUrl]) {
              imageCache[item.imageUrl] = await fetchImageAsBase64(item.imageUrl);
            }
            const base64 = imageCache[item.imageUrl];
            const format = base64.includes('image/png') ? 'PNG' : 'JPEG';
            doc.addImage(base64, format, imgX + 2, imgY + 2, imageWidth - 4, imageHeight - 4, undefined, 'FAST');
          } catch (error) {
            doc.setTextColor(148, 163, 184);
            doc.setFontSize(8);
            doc.text('Gorsel yuklenemedi', imgX + 6, imgY + imageHeight / 2);
          }
        } else {
          doc.setTextColor(148, 163, 184);
          doc.setFontSize(8);
          doc.text('Gorsel yok', imgX + 16, imgY + imageHeight / 2);
        }

        doc.setTextColor(71, 85, 105);
        doc.setFontSize(8);
        doc.text(`${i + 1}`, imgX + imageWidth / 2, imgY + imageHeight + 12, { align: 'center' });
      }

      y += cardHeight + cardGap;
    }

    doc.save(`content-presentation-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="space-y-4 px-3 sm:px-4 md:px-6 py-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Content Presentation Table</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Post detaylari solda, gorseller sagda sunum tablosu</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()}>Print</Button>
          <Button variant="outline" onClick={handleExportPdf}>PDF Export</Button>
          <Button onClick={handleExportExcel}>Excel Export</Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <select
            value={filters.account_name}
            onChange={(e) => setFilters((prev) => ({ ...prev, account_name: e.target.value }))}
            className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
          >
            <option value="">All Accounts</option>
            <option value="Antedon">Antedon</option>
            <option value="Alinda">Alinda</option>
            <option value="Claros">Claros</option>
            <option value="Residence">Residence</option>
            <option value="Lush">Lush</option>
            <option value="Suites">Suites</option>
          </select>
          <select
            value={filters.content_type}
            onChange={(e) => setFilters((prev) => ({ ...prev, content_type: e.target.value }))}
            className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
          >
            <option value="">All Types</option>
            {Object.entries(CONTENT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={filters.responsible_person}
            onChange={(e) => setFilters((prev) => ({ ...prev, responsible_person: e.target.value }))}
            className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
          >
            <option value="">Tum Sorumlular</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>{user.full_name || user.email}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Arama..."
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
            className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
          />
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-auto max-h-[72vh]">
          <table className="min-w-[1400px] w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 cursor-pointer select-none"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {loading ? (
                <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-500">Yukleniyor...</td></tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-gray-500">Kayit bulunamadi.</td></tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-5 align-middle text-center text-sm text-gray-800 dark:text-gray-100">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ContentPresentationTable;
